import { Component, OnInit, inject } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ProductsService } from '../../products/products.service';
import {
  CreateInventoryItemDto,
  CreateInventoryDto,
  LocationDto,
} from '../inventory.model';
import { InventoryService } from '../inventory.service';
import { Product, ProductVariant } from '../../products/products.model';

import { ToastrService } from 'ngx-toastr';
import { NgSelectModule } from '@ng-select/ng-select';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-add-inventory',
  templateUrl: './add-inventory.component.html',
  styleUrl: './add-inventory.component.scss',
  imports: [CommonModule, FormsModule, ReactiveFormsModule, NgSelectModule],
})
export class AddInventoryComponent implements OnInit {
  private fb = inject(FormBuilder);
  private inventoryService = inject(InventoryService);
  private productService = inject(ProductsService);
  private toastr = inject(ToastrService);
  selectedLocationId: string = '';

  inventoryForm!: FormGroup;
  products: Product[] = [];
  variants: ProductVariant[] = [];
  locations: LocationDto[] = [];
  inventoryItems: any[] = [];

  ngOnInit(): void {
    this.inventoryForm = this.fb.group({
      productId: ['', Validators.required],
      variantId: ['', Validators.required],
      locationId: ['', Validators.required],
      quantity: [0, [Validators.min(1)]],
      outerPackaging: [''],
      batchNumber: [''],
      expiryDate: [''],
    });

    this.loadProducts();
    this.loadLocations();

    this.inventoryForm.get('productId')?.valueChanges.subscribe((id) => {
      const product: any = this.products.find((p) => p.id === id);
      this.variants = product?.variants || [];
      this.inventoryForm.patchValue({ variantId: '' });
    });
  }

  loadProducts() {
    this.productService
      .getProducts({ page: 1, limit: 1000 }, '',false)
      .subscribe((res) => {
        this.products = res.data;
      });
  }

  loadLocations() {
    this.inventoryService.getAllLocations().subscribe((res) => {
      this.locations = res;
    });
  }

  calculateQuantity(): number {
    const formVal = this.inventoryForm.value;
    const product = this.products.find((p) => p.id === formVal.productId);
    const outer = +formVal.outerPackaging || 0;
    return product?.innerPackaging ? outer * product.innerPackaging : 0;
  }

  addItemToTable() {
    const formVal = this.inventoryForm.value;
    const quantity =
      formVal.quantity > 0 ? formVal.quantity : this.calculateQuantity();

    if (!formVal.variantId || !formVal.locationId || quantity <= 0) return;

    // store selected location separately
    this.selectedLocationId = formVal.locationId;

    const item: any = {
      variantId: formVal.variantId,
      quantity,
      batchNumber: formVal.batchNumber,
      expiryDate: formVal.expiryDate,
      productName: this.getProductName(formVal.productId),
      variantName: this.getVariantName(formVal.productId, formVal.variantId),
    };

    this.inventoryItems.push(item);
    this.inventoryForm.reset();
    this.variants = [];
    if (this.selectedLocationId) {
      this.inventoryForm.patchValue({ locationId: this.selectedLocationId });
    }
  }

  removeItem(index: number) {
    this.inventoryItems.splice(index, 1);
  }

  getProductName(id: string): string {
    return this.products.find((p) => p.id === id)?.name || '';
  }

  getVariantName(productId: string, variantId: string): string {
    const product: any = this.products.find((p) => p.id === productId);
    return (
      product?.variants?.find((v: any) => v.id === variantId)?.variantName || ''
    );
  }

  getLocationName(locationId: string): string {
    const loc = this.locations.find((location) => location.id === locationId);
    return loc ? loc.name : '';
  }

  submitInventory() {
    if (!this.inventoryItems.length || !this.selectedLocationId) return;

    const timestamp = new Date().getTime();
    const simpleCode = 'INV-' + timestamp;

    const itemsCopy = this.inventoryItems.map((item) => {
      const { productId, productName, variantName, ...rest } = item;
      return { ...rest };
    });

    const dto: any = {
      code: simpleCode,
      locationId: this.selectedLocationId,
      items: itemsCopy,
    };

    this.inventoryService.addInventory(dto).subscribe(() => {
      this.toastr.success('Inventory added successfully');
      this.inventoryItems = [];
      this.selectedLocationId = '';
    });
  }
}
